// Module registry that drives the client nav + routes.
//
// Seeded at scaffold time (a snapshot of the backend modules.config.js) so the
// SPA has an instant, offline-capable first paint — then reconciled at runtime
// from GET /api/meta/modules so edits to modules.config.js show up WITHOUT a
// client rebuild. The runtime copy is the single source of truth; the seed is
// only the first-paint fallback. Consume via useModules().
import { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiRoot } from './lib/domainResolver';

export const seedModules = {
  "core": {
    "key": "core",
    "label": "Core",
    "icon": "layout-dashboard",
    "models": [
      "User",
      "Role",
      "Permission",
      "ApiToken"
    ],
    "routes": [
      "/",
      "/users",
      "/roles",
      "/permissions",
      "/api-tokens",
      "/settings",
      "/profile"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "manage_tokens",
      "manage_settings"
    ],
    "instanceScoped": []
  },
  "scraper": {
    "key": "scraper",
    "label": "Web Scraper",
    "icon": "globe",
    "models": [
      "ScrapeJob",
      "ScrapePage"
    ],
    "routes": [
      "/scraper",
      "/scraper/:id"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "export"
    ],
    "instanceScoped": []
  },
  "video": {
    "key": "video",
    "label": "Videos",
    "icon": "video",
    "models": [
      "Video",
      "VideoView"
    ],
    "routes": [
      "/videos",
      "/videos/:id"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "share"
    ],
    "instanceScoped": []
  },
  "tools": {
    "key": "tools",
    "label": "Utility Tools",
    "icon": "wrench",
    "models": [
      "UtilityJob",
      "DataMigrationRun",
      "DataMigrationStep",
      "DbMigrationRun",
      "DbMigrationStep"
    ],
    "routes": [
      "/tools",
      "/tools/video-compress",
      "/tools/data-migration",
      "/tools/db-migration"
    ],
    "actions": [
      "view",
      "create",
      "manage"
    ],
    "instanceScoped": []
  },
  "github": {
    "key": "github",
    "label": "GitHub",
    "icon": "github",
    "models": [
      "GithubConnection"
    ],
    "routes": [
      "/github",
      "/github/callback"
    ],
    "actions": [
      "view",
      "connect",
      "disconnect"
    ],
    "instanceScoped": []
  },
  "development": {
    "key": "development",
    "label": "Development",
    "icon": "code",
    "models": [
      "GitApp",
      "GitAppChange",
      "GitAppTicket",
      "BackgroundJob"
    ],
    "routes": [
      "/git-apps",
      "/git-apps/:id"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "commit",
      "deploy"
    ],
    "instanceScoped": []
  }
};
export default seedModules;

// Shared across hook instances: once the live registry arrives, later mounts
// (and a second consumer on the same page) start from the fresh copy.
let cache = seedModules;

/** Live module registry — seeded from the build-time copy, reconciled from the API. */
export function useModules() {
  const [modules, setModules] = useState(cache);
  useEffect(() => {
    let active = true;
    axios
      .get(`${getApiRoot()}/api/meta/modules`, { withCredentials: true })
      .then((res) => {
        if (active && res.data && typeof res.data === 'object') {
          cache = res.data;
          setModules(res.data);
        }
      })
      .catch(() => {
        /* offline / not yet reachable — keep the seeded copy */
      });
    return () => {
      active = false;
    };
  }, []);
  return modules;
}
